import { useShallow } from "zustand/react/shallow";
import { useNetworkStore } from "@/features/network-topology/store";
import type { ConnectionType, LogEvent, NetworkNode } from "@/features/network-topology/types";

/**
 * NetworkGraph / Panel 共通の購読フック。
 * useShallow で nodes 配列の参照同値比較。
 */
export interface NetworkTopologyView {
  nodes: NetworkNode[];
  selectedNodeId: string | null;
  selectedEdgeChildId: string | null;
  events: LogEvent[];
}

export function useNetworkTopology(): NetworkTopologyView {
  return useNetworkStore(
    useShallow(
      (s): NetworkTopologyView => ({
        nodes: s.nodes,
        selectedNodeId: s.selectedNodeId,
        selectedEdgeChildId: s.selectedEdgeChildId,
        events: s.events,
      }),
    ),
  );
}

/** アクションのみまとめて返すフック（再レンダ抑制用） */
export interface NetworkActions {
  selectNode: (id: string | null) => void;
  selectEdge: (childNodeId: string | null) => void;
  disconnectNode: (id: string) => void;
  startConnect: (id: string) => void;
  forceOnline: (id: string) => void;
  addNode: (spec: Parameters<ReturnType<typeof useNetworkStore.getState>["addNode"]>[0]) => string | null;
  removeNode: (id: string) => void;
  disconnectLink: (childNodeId: string) => void;
  startConnectLink: (childNodeId: string) => void;
  setLinkType: (childNodeId: string, linkType: ConnectionType) => void;
  setFaulted: (id: string, faulted: boolean) => void;
  injectRandomFault: () => string | null;
  clearAllFaults: () => void;
  resetToDefault: () => void;
  clearEvents: () => void;
}

export function useNetworkActions(): NetworkActions {
  return useNetworkStore(
    useShallow(
      (s): NetworkActions => ({
        selectNode: s.selectNode,
        selectEdge: s.selectEdge,
        disconnectNode: s.disconnectNode,
        startConnect: s.startConnect,
        forceOnline: s.forceOnline,
        addNode: s.addNode,
        removeNode: s.removeNode,
        disconnectLink: s.disconnectLink,
        startConnectLink: s.startConnectLink,
        setLinkType: s.setLinkType,
        setFaulted: s.setFaulted,
        injectRandomFault: s.injectRandomFault,
        clearAllFaults: s.clearAllFaults,
        resetToDefault: s.resetToDefault,
        clearEvents: s.clearEvents,
      }),
    ),
  );
}
